// src/hooks/useLinkLists.js
import React from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function apiUrl(path) {
    // English comment: Build absolute URL when API_BASE is set, otherwise use relative
    if (!path.startsWith("/")) path = `/${path}`;
    return API_BASE ? `${API_BASE}${path}` : path;
}

async function readError(res) {
    // English comment: Try to parse JSON errors, fallback to text/html
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) {
        const j = await res.json().catch(() => null);
        if (j?.message) return String(j.message);
        if (j?.error) return String(j.error);
        return JSON.stringify(j ?? {});
    }
    return await res.text();
}

async function apiGet(path) {
    const res = await fetch(apiUrl(path), { credentials: "include" });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
}

async function apiPost(path, body) {
    const res = await fetch(apiUrl(path), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
}

async function apiPatch(path, body) {
    const res = await fetch(apiUrl(path), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
}

async function apiDelete(path) {
    const res = await fetch(apiUrl(path), { method: "DELETE", credentials: "include" });
    if (!res.ok) throw new Error(await readError(res));
    return res.json();
}

export default function useLinkLists({ currentUserId }) {
    const [items, setItems] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState("");

    const fetchLinkLists = React.useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            // English comment: Include tags so the page can render them.
            const data = await apiGet("/api/link-lists?limit=500&offset=0&include_tags=true");
            setItems(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(String(e?.message || e));
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchLinkLists();
    }, [fetchLinkLists]);

    const createLinkList = React.useCallback(async (url, tags = [], { ai_rating = false, ai_reason = null, ai_probability = null } = {}) => {
        setError("");
        const u = (url || "").trim();
        if (!u) throw new Error("URL ist leer");
        if (!currentUserId || Number(currentUserId) <= 0) {
            throw new Error("currentUserId fehlt (Auth-Kontext anbinden)");
        }

        await apiPost("/api/link-lists", {
            url: u,
            created_by: Number(currentUserId),
            is_active: true,
            tags: Array.isArray(tags) ? tags : [],
            ai_rating: !!ai_rating,
            ai_reason,
            ai_probability,
        });

        await fetchLinkLists();
    }, [currentUserId, fetchLinkLists]);

    const updateLinkList = React.useCallback(async (id, patch) => {
        setError("");
        if (!id) throw new Error("id fehlt");
        const body = {};

        if (patch?.url !== undefined) body.url = patch.url;
        if (patch?.is_active !== undefined) body.is_active = patch.is_active;

        if (patch?.ai_rating !== undefined) body.ai_rating = patch.ai_rating;
        if (patch?.ai_reason !== undefined) body.ai_reason = patch.ai_reason;
        if (patch?.ai_probability !== undefined) body.ai_probability = patch.ai_probability;

        // English comment: Replace tags when provided.
        if (patch?.tags !== undefined) body.tags = Array.isArray(patch.tags) ? patch.tags : [];

        await apiPatch(`/api/link-lists/${id}`, body);
        await fetchLinkLists();
    }, [fetchLinkLists]);

    const toggleLinkList = React.useCallback(async (item) => {
        setError("");
        await apiPost(`/api/link-lists/${item.id}/toggle`, { is_active: !item.is_active });
        await fetchLinkLists();
    }, [fetchLinkLists]);

    const deleteLinkList = React.useCallback(async (id) => {
        setError("");
        await apiDelete(`/api/link-lists/${id}`);
        await fetchLinkLists();
    }, [fetchLinkLists]);

    return {
        items,
        loading,
        error,
        fetchLinkLists,
        createLinkList,
        updateLinkList,
        toggleLinkList,
        deleteLinkList,
    };
}
